import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  designListTemplates, designGetTemplate, designUpdateTemplate, designPreviewContext,
  designDealerSearch, getAiPoints, formsList,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Search, ChevronLeft, Eye, Code, Save, Share2, MessageSquare,
  Home as HomeIcon, Sparkles, Lock, Plus, Users as UsersIcon, X, Wand2, Zap, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import EmailDesigner from "@/components/EmailDesigner";
import DesignTriggersPanel from "@/components/DesignTriggersPanel";

function renderTokens(html, ctx) {
  if (!html) return "";
  const withForms = html.replace(/\{\{\s*FORM_(LINK|BUTTON)\s*:\s*([^}\s]+)\s*\}\}/gi, (_, kind, ref) =>
    kind.toUpperCase() === "LINK"
      ? "#form-preview"
      : `<a href="#form-preview" style="display:inline-block;background:#0b2545;color:#fff;text-decoration:none;padding:14px 28px;border-radius:999px;font-weight:bold;font-family:Arial,sans-serif;font-size:15px;">Open my form (${ref})</a>`);
  return withForms.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => {
    const v = ctx?.[k];
    return v === undefined || v === null ? "" : String(v);
  });
}

export default function DesignTab({ userRecordId, userId }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [ctx, setCtx] = useState({});
  const [footerHtml, setFooterHtml] = useState("");
  const [aiPoints, setAiPoints] = useState(null);
  const [selected, setSelected] = useState(null); // full template
  const [loadingOne, setLoadingOne] = useState(false);
  const [view, setView] = useState("preview"); // preview | edit
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [forms, setForms] = useState([]);
  useEffect(() => { formsList(userRecordId).then((r) => setForms((r.forms || []).filter((f) => f.active && f.form_id))).catch(() => setForms([])); }, [userRecordId]);
  const [editId, setEditId] = useState(null);
  const [dealers, setDealers] = useState([]); // selected dealers [{id,name}]
  const [dealerQuery, setDealerQuery] = useState("");
  const [dealerResults, setDealerResults] = useState([]);
  const [searchingDealers, setSearchingDealers] = useState(false);
  const htmlRef = useRef(null);

  function insertHtmlToken(token) {
    const ta = htmlRef.current;
    setDraft((d) => {
      const html = d?.html || "";
      if (!ta) return { ...d, html: html + token };
      const s = ta.selectionStart ?? html.length;
      const e = ta.selectionEnd ?? html.length;
      return { ...d, html: html.slice(0, s) + token + html.slice(e) };
    });
  }

  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState("");

  const load = useCallback(() => {
    if (!userRecordId) return;
    setLoading(true);
    Promise.all([
      designListTemplates(userRecordId, showArchived),
      designPreviewContext(userRecordId).catch(() => ({ context: {} })),
    ])
      .then(([res, c]) => {
        setItems(res.items || []);
        setIsAdmin(!!res.is_admin);
        setCtx(c.context || {});
        setFooterHtml(c.footer_html || "");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load templates"))
      .finally(() => setLoading(false));
  }, [userRecordId, showArchived]);

  async function setArchived(it, hidden) {
    setArchivingId(it.id);
    try {
      await designUpdateTemplate(it.id, { user_record_id: userRecordId, status: hidden ? "hidden" : "published" });
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success(hidden ? `"${it.title}" archived — find it under Archived to restore.` : `"${it.title}" restored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setArchivingId(""); }
  }

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (userRecordId) getAiPoints(userRecordId).then(setAiPoints).catch(() => {});
  }, [userRecordId, creating]);

  async function open(it) {
    setLoadingOne(true);
    setSelected({ id: it.id });
    try {
      const t = await designGetTemplate(it.id, userRecordId);
      setSelected(t);
      setDraft({
        html: t.html, title: t.title, subject: t.subject,
        share_to_network: t.share_to_network, show_in_chat: t.show_in_chat,
        home: t.home, show_on_splash: t.show_on_splash,
      });
      setDealers(t.selected_dealers || []);
      setDealerQuery(""); setDealerResults([]);
      setView("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open template");
      setSelected(null);
    } finally {
      setLoadingOne(false);
    }
  }

  async function save() {
    if (!selected?.can_edit) return;
    setSaving(true);
    try {
      await designUpdateTemplate(selected.id, {
        user_record_id: userRecordId, ...draft,
        selected_by_dealers: dealers.map((d) => d.id),
      });
      toast.success("Template saved");
      setItems((prev) => prev.map((x) => x.id === selected.id ? {
        ...x, title: draft.title, share_to_network: draft.share_to_network,
        show_in_chat: draft.show_in_chat, home: draft.home, show_on_splash: draft.show_on_splash,
      } : x));
      setSelected((s) => ({ ...s, ...draft, selected_dealers: dealers }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function runDealerSearch(term) {
    setDealerQuery(term);
    if (!term.trim()) { setDealerResults([]); return; }
    setSearchingDealers(true);
    try {
      const res = await designDealerSearch(userRecordId, term.trim());
      const chosen = new Set(dealers.map((d) => d.id));
      setDealerResults((res.dealers || []).filter((d) => !chosen.has(d.id)));
    } catch {
      setDealerResults([]);
    } finally {
      setSearchingDealers(false);
    }
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => (i.title + " " + i.slug).toLowerCase().includes(s));
  }, [items, q]);

  const previewHtml = useMemo(
    () => renderTokens(draft?.html ?? selected?.html, ctx),
    [draft, selected, ctx]
  );

  // ---- Create email designer ----
  if (creating) {
    return (
      <EmailDesigner
        userRecordId={userRecordId}
        isAdmin={isAdmin}
        editTemplateId={editId}
        footerHtml={footerHtml}
        onCancel={() => { setCreating(false); setEditId(null); }}
        onDone={() => { setCreating(false); setEditId(null); load(); }}
      />
    );
  }

  // ---- Detail view ----
  if (selected) {
    const canEdit = selected.can_edit;
    return (
      <div data-testid="design-detail">
        <div className="flex items-center justify-between gap-2 mb-3">
          <button
            onClick={() => setSelected(null)}
            className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
            data-testid="design-back-btn"
          >
            <ChevronLeft size={16} /> All templates
          </button>
          <div className="flex rounded-lg bg-zinc-100 p-0.5">
            <button
              onClick={() => setView("preview")}
              className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1 ${view === "preview" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
              data-testid="design-view-preview"
            >
              <Eye size={13} /> Preview
            </button>
            <button
              onClick={() => setView("edit")}
              className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1 ${view === "edit" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
              data-testid="design-view-edit"
            >
              <Code size={13} /> Edit
            </button>
            <button
              onClick={() => setView("triggers")}
              className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1 ${view === "triggers" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
              data-testid="design-view-triggers"
            >
              <Zap size={13} /> Triggers
            </button>
          </div>
        </div>

        {loadingOne || !selected.html && selected.html !== "" ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
        ) : view === "triggers" ? (
          <DesignTriggersPanel
            userRecordId={userRecordId}
            userId={userId}
            template={selected}
            onTemplateChanged={(patch) => { setSelected((s) => ({ ...s, ...patch })); setDraft((d) => ({ ...d, ...patch })); setItems((prev) => prev.map((x) => (x.id === selected.id ? { ...x, ...patch } : x))); }}
          />
        ) : view === "preview" ? (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 flex items-center gap-1.5">
              <Sparkles size={12} className="text-violet-500" />
              Merge fields shown with sample data (e.g. {ctx.first_name || "Jane"} · {ctx.dealer_name || "you"}).
            </div>
            <div className="rounded-2xl border border-zinc-200 overflow-hidden bg-white">
              <iframe
                title="template-preview"
                sandbox=""
                srcDoc={`<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"></head><body style="margin:0">${previewHtml}</body></html>`}
                className="w-full"
                style={{ height: "70vh", border: "0" }}
                data-testid="design-preview-frame"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="design-edit-form">
            {!canEdit && (
              <div className="rounded-lg bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-center gap-2">
                <Lock size={13} /> This template belongs to another dealer — view only.
              </div>
            )}
            {canEdit && (
              <Button
                type="button" variant="outline"
                onClick={() => { setEditId(selected.id); setCreating(true); }}
                className="rounded-lg h-9 w-full border-violet-200 text-violet-700 hover:bg-violet-50"
                data-testid="design-edit-in-designer-btn"
              >
                <Wand2 size={14} className="mr-1" /> Edit in designer (text &amp; images)
              </Button>
            )}
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">Title</div>
              <Input
                value={draft.title}
                disabled={!canEdit}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                className="h-10 rounded-lg"
                data-testid="design-title-input"
              />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">Email subject</div>
              <Input
                value={draft.subject}
                disabled={!canEdit}
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                placeholder="e.g. A little something for you, {{first_name}}"
                className="h-10 rounded-lg"
                data-testid="design-subject-input"
              />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1 flex items-center justify-between">
                <span>HTML</span>
                <span className="text-[10px] text-zinc-400">Use {"{{first_name}}"}, {"{{dealer_name}}"}, {"{{company}}"}…</span>
              </div>
              <Textarea
                ref={htmlRef}
                value={draft.html}
                disabled={!canEdit}
                onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))}
                rows={12}
                className="rounded-lg font-mono text-xs"
                spellCheck={false}
                data-testid="design-html-input"
              />
              {canEdit && (
                <div className="mt-2">
                  <div className="text-[11px] font-medium text-zinc-500 mb-1">
                    Insert a variable (auto-fills per recipient)
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="design-variables">
                    {[
                      { label: "First name", token: "{{first_name}}" },
                      { label: "Full name", token: "{{full_name}}" },
                      { label: "Company", token: "{{customer_company}}" },
                      { label: "Total revenue", token: "{{total_revenue}}" },
                      { label: "Last order (items + total)", token: "{{LAST_ORDER}}" },
                      { label: "Order #", token: "{{ORDER_NUMBER}}" },
                      { label: "Order status", token: "{{ORDER_STATUS}}" },
                      { label: "Order items (photo · price · MSRP)", token: "{{ORDER_ITEMS}}" },
                      { label: "Review buttons (per product · earn rewards)", token: "{{REVIEW_ITEMS}}" },
                      { label: "Order total", token: "{{ORDER_TOTAL}}" },
                      { label: "Shipping address", token: "{{SHIPPING_ADDRESS}}" },
                      { label: "Billing address", token: "{{BILLING_ADDRESS}}" },
                      { label: "Weight", token: "{{ORDER_WEIGHT}}" },
                      { label: "Ambassador line", token: "{{AMBASSADOR_LINE}}" },
                      { label: "Return policy", token: "{{RETURN_POLICY}}" },
                      { label: "Tracking link", token: "{{TRACKING_URL}}" },
                      { label: "Tracking #", token: "{{TRACKING_NUMBER}}" },
                      { label: "Customer name", token: "{{CUSTOMER_NAME}}" },
                      { label: "Reward amount (review share)", token: "{{REWARD_AMOUNT}}" },
                      { label: "Rewards balance", token: "{{REWARDS_BALANCE}}" },
                      { label: "Reviewed product", token: "{{PRODUCT_NAME}}" },
                      { label: "Shop link", token: "{{SHOP_URL}}" },
                      { label: "CTA: Approve me button (approved = 1)", token: "{{APPROVE_BUTTON}}" },
                      { label: "CTA: I'm interested button (prospect = Y)", token: "{{PROSPECT_BUTTON}}" },
                    ].map((v) => (
                      <button
                        key={v.token}
                        type="button"
                        onClick={() => insertHtmlToken(v.token)}
                        className="px-2.5 py-1 rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-[11px] font-medium hover:bg-violet-100 transition-colors"
                        data-testid={`design-var-${v.token.replace(/[^a-z_]/gi, "")}`}
                      >
                        + {v.label}
                      </button>
                    ))}
                  </div>
                  {forms.length > 0 && (
                    <div className="mt-2" data-testid="design-form-variables">
                      <div className="text-[11px] font-medium text-zinc-500 mb-1">Insert a form (each recipient gets their own personal link)</div>
                      <div className="flex flex-wrap gap-1.5">
                        {forms.map((f) => (
                          <span key={f.id} className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 overflow-hidden">
                            <button type="button" onClick={() => insertHtmlToken(`{{FORM_BUTTON:${f.form_id}}}`)} className="px-2.5 py-1 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100 transition-colors" data-testid={`design-form-button-${f.form_id}`}>+ {f.name} button</button>
                            <button type="button" onClick={() => insertHtmlToken(`{{FORM_LINK:${f.form_id}}}`)} className="px-2 py-1 border-l border-emerald-200 text-emerald-700 text-[11px] hover:bg-emerald-100 transition-colors" title="Insert just the link URL" data-testid={`design-form-link-${f.form_id}`}>link</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1 rounded-xl border border-zinc-200 divide-y divide-zinc-100">
              <ToggleRow icon={Share2} label="Share — show on all dealer pages"
                checked={draft.share_to_network} disabled={!canEdit} testId="design-toggle-share"
                onChange={(v) => setDraft((d) => ({ ...d, share_to_network: v }))} />
              <ToggleRow icon={MessageSquare} label="Show in chat"
                checked={draft.show_in_chat} disabled={!canEdit} testId="design-toggle-chat"
                onChange={(v) => setDraft((d) => ({ ...d, show_in_chat: v }))} />
              <ToggleRow icon={HomeIcon} label="Show on Home"
                checked={draft.home} disabled={!canEdit} testId="design-toggle-home"
                onChange={(v) => setDraft((d) => ({ ...d, home: v }))} />
              <ToggleRow icon={Eye} label="Show on splash"
                checked={draft.show_on_splash} disabled={!canEdit} testId="design-toggle-splash"
                onChange={(v) => setDraft((d) => ({ ...d, show_on_splash: v }))} />
            </div>

            {canEdit && (
              <div className="rounded-xl border border-zinc-200 p-3 space-y-2" data-testid="design-share-dealers">
                <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-700">
                  <UsersIcon size={15} className="text-zinc-400" /> Share with specific dealers
                </div>
                <p className="text-[11px] text-zinc-400 -mt-1">
                  These dealers will see this email even when Share is off. (Share above = everyone.)
                </p>
                {dealers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="design-selected-dealers">
                    {dealers.map((d) => (
                      <span key={d.id} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 text-xs rounded-full pl-2.5 pr-1 py-1">
                        {d.name}
                        <button
                          onClick={() => setDealers((prev) => prev.filter((x) => x.id !== d.id))}
                          className="rounded-full hover:bg-violet-200 p-0.5"
                          data-testid={`design-remove-dealer-${d.id}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={dealerQuery}
                    onChange={(e) => runDealerSearch(e.target.value)}
                    placeholder="Search dealers by name or email…"
                    className="h-9 rounded-lg pl-8 text-sm"
                    data-testid="design-dealer-search"
                  />
                </div>
                {(searchingDealers || dealerResults.length > 0) && (
                  <ul className="max-h-40 overflow-auto rounded-lg border border-zinc-100 divide-y divide-zinc-50">
                    {searchingDealers && <li className="px-3 py-2 text-xs text-zinc-400">Searching…</li>}
                    {dealerResults.map((d) => (
                      <li key={d.id}>
                        <button
                          onClick={() => {
                            setDealers((prev) => [...prev, { id: d.id, name: d.name }]);
                            setDealerResults((prev) => prev.filter((x) => x.id !== d.id));
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-violet-50/50"
                          data-testid={`design-add-dealer-${d.id}`}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm text-zinc-800 truncate">{d.name}</span>
                            <span className="block text-[11px] text-zinc-400 truncate">{d.email}</span>
                          </span>
                          <Plus size={14} className="text-violet-500 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {canEdit && (
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-xl h-11 w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="design-save-btn"
              >
                {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : <Save size={15} className="mr-1" />}
                Save changes
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- List view ----
  return (
    <div data-testid="design-tab">
      <div className="rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-700 text-white p-5 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Marketing AI</h3>
            <p className="text-sm text-white/80">
              Create, preview and edit your HTML email templates.
            </p>
          </div>
          <Button
            onClick={() => setCreating(true)}
            className="rounded-xl h-9 bg-white text-zinc-900 hover:bg-zinc-100 shrink-0"
            data-testid="design-create-btn"
          >
            <Plus size={15} className="mr-1" /> Create email
          </Button>
        </div>
        {aiPoints && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs text-white" data-testid="design-ai-points">
            <Sparkles size={12} className="text-amber-300" />
            <strong>{aiPoints.balance}</strong> AI points available
            <span className="text-white/60">· {aiPoints.earned} earned + {aiPoints.bonus} bonus</span>
          </div>
        )}
      </div>
      <div className="relative mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates…"
            className="h-10 rounded-lg pl-9"
            data-testid="design-search"
          />
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={`h-10 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${showArchived ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
            data-testid="design-archived-toggle"
          >
            <Archive size={13} /> {showArchived ? "Showing archived" : "Archived"}
          </button>
        )}
      </div>
      {showArchived && (
        <p className="text-xs text-zinc-500 mb-2" data-testid="design-archived-note">
          Archived emails are hidden from every list (status “hidden” in Airtable). Restore one to bring it back.
        </p>
      )}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : (
        <ul className="space-y-2" data-testid="design-list">
          {filtered.map((it) => (
            <li key={it.id} className="flex items-center gap-2">
              <button
                onClick={() => open(it)}
                className="flex-1 min-w-0 flex items-center gap-3 rounded-xl border border-zinc-200 p-2.5 text-left hover:border-violet-300 hover:bg-violet-50/40 transition-colors"
                data-testid={`design-item-${it.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-zinc-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {it.image_url
                    ? <img src={it.image_url} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                    : <Code size={16} className="text-zinc-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{it.title}</div>
                  <div className="text-[11px] text-zinc-400 truncate font-mono">{it.slug}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {it.visibility === "private" ? (
                    <Tag color="amber" testId={`design-visibility-${it.id}`}>Private{it.owner_name ? ` · ${it.owner_name}` : ""}</Tag>
                  ) : it.visibility === "shared" ? (
                    <Tag color="emerald" testId={`design-visibility-${it.id}`}>Shared</Tag>
                  ) : (
                    <Tag color="zinc" testId={`design-visibility-${it.id}`}>Admin only</Tag>
                  )}
                  {it.show_in_chat && <Tag color="sky">Chat</Tag>}
                  {!it.can_edit && <Lock size={13} className="text-zinc-300 self-center" />}
                </div>
              </button>
              {it.can_edit && (
                <button
                  type="button"
                  onClick={() => setArchived(it, !showArchived)}
                  disabled={archivingId === it.id}
                  title={showArchived ? "Restore to the list" : "Archive (remove from the list; restore anytime under Archived)"}
                  className={`h-9 w-9 shrink-0 rounded-lg border inline-flex items-center justify-center transition-colors ${showArchived ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-zinc-200 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50"}`}
                  data-testid={`design-${showArchived ? "restore" : "archive"}-${it.id}`}
                >
                  {archivingId === it.id ? <Loader2 size={14} className="animate-spin" /> : showArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                </button>
              )}
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="text-sm text-zinc-500 italic text-center py-8">{showArchived ? "Nothing archived." : "No templates found."}</li>
          )}
        </ul>
      )}
    </div>
  );
}

function Tag({ children, color, testId }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-800",
    zinc: "bg-zinc-100 text-zinc-600",
  };
  return <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 whitespace-nowrap ${map[color] || map.zinc}`} data-testid={testId}>{children}</span>;
}

function ToggleRow({ icon: Icon, label, checked, disabled, onChange, testId }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div className="flex items-center gap-2 text-sm text-zinc-700">
        <Icon size={15} className="text-zinc-400" /> {label}
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
